import { useEffect, useState, useCallback } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MessageSquare, Trash2 } from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import { apiGet, apiPost, apiDelete, ApiError } from '@shared/libs/apiClient'
import { useAuth } from '@shared/stores/AuthContext'

export type ForumCategory = 'perspective' | 'commentary'

export interface ForumPost {
    id: string
    category: ForumCategory
    title: string
    content: string
    authorId: string
    authorName: string
    createdAt: string
    commentCount?: number
}

const COPY: Record<ForumCategory, { title: string; eyebrow: string; lede: string; cta: string }> = {
    perspective: {
        title: 'Perspectives',
        eyebrow: 'Discussion Forum',
        lede: 'Longer opinion and position pieces from our community of authors, reviewers and readers on politics, governance and public policy.',
        cta: 'Share a perspective'
    },
    commentary: {
        title: 'Commentary',
        eyebrow: 'Discussion Forum',
        lede: 'Short, timely responses and commentary on current debates, published research and questions of the day.',
        cta: 'Post a commentary'
    }
}

function fmtDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    } catch {
        return ''
    }
}

function snippetOf(content: string, n = 220): string {
    const clean = content.replace(/\s+/g, ' ').trim()
    return clean.length > n ? clean.slice(0, n - 1).trim() + '…' : clean
}

export default function DiscussionForum({ category }: { category: ForumCategory }) {
    const { user } = useAuth()
    const copy = COPY[category]

    const [posts, setPosts] = useState<ForumPost[]>([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)

    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [formMsg, setFormMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

    const load = useCallback(() => {
        setLoading(true)
        setLoadError(null)
        apiGet<{ success: boolean; posts: ForumPost[] }>(`/forum?category=${category}`)
            .then((r) => setPosts(r.posts ?? []))
            .catch((e) => setLoadError(e instanceof ApiError ? e.message : 'Failed to load discussions.'))
            .finally(() => setLoading(false))
    }, [category])

    useEffect(() => {
        load()
        setTitle('')
        setContent('')
        setFormMsg(null)
    }, [load])

    const submit = async (e: FormEvent) => {
        e.preventDefault()
        if (!title.trim() || !content.trim()) {
            setFormMsg({ kind: 'err', text: 'Please add both a title and some content.' })
            return
        }
        setSubmitting(true)
        setFormMsg(null)
        try {
            const r = await apiPost<{ success: boolean; post: ForumPost }>('/forum', {
                category,
                title: title.trim(),
                content: content.trim()
            })
            setPosts((prev) => [{ ...r.post, commentCount: 0 }, ...prev])
            setTitle('')
            setContent('')
            setFormMsg({ kind: 'ok', text: 'Your discussion has been posted.' })
        } catch (err) {
            setFormMsg({ kind: 'err', text: err instanceof ApiError ? err.message : 'Could not post. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const canModerate = (authorId: string) =>
        !!user && (user.role === 'admin' || user.role === 'editor' || user.id === authorId)

    const remove = async (id: string) => {
        if (!window.confirm('Delete this discussion? This cannot be undone.')) return
        try {
            await apiDelete(`/forum/${id}`)
            setPosts((prev) => prev.filter((p) => p.id !== id))
        } catch (err) {
            alert(err instanceof ApiError ? err.message : 'Failed to delete.')
        }
    }

    return (
        <>
            <SEO
                title={`${copy.title} — Discussion Forum`}
                description={copy.lede}
                path={`/discussion/${category}`}
            />
            <PageHeader eyebrow={copy.eyebrow} title={copy.title} lede={copy.lede} />

            <section className="section">
                <div className="container-prose max-w-3xl mx-auto">
                    {user ? (
                        <form
                            onSubmit={submit}
                            className="p-6 md:p-7 mb-10"
                            style={{ background: 'var(--color-paper-50)', border: '1px solid var(--color-rule-strong)', borderTop: '2px solid var(--color-accent-500)' }}
                        >
                            <div className="eyebrow">{copy.cta}</div>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Title"
                                maxLength={300}
                                className="input mt-4"
                            />
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder={`Write your ${category}…`}
                                rows={5}
                                className="input mt-3"
                            />
                            {formMsg && (
                                <div
                                    className="mt-3 text-[13.5px]"
                                    style={{ color: formMsg.kind === 'ok' ? '#1E4A2A' : '#8B1A2D' }}
                                >
                                    {formMsg.text}
                                </div>
                            )}
                            <div className="mt-4 flex items-center gap-3">
                                <button type="submit" disabled={submitting} className="btn btn-accent">
                                    {submitting ? 'Posting…' : 'Post discussion'}
                                </button>
                                <span className="text-[12.5px]" style={{ color: 'var(--color-ink-400)' }}>
                                    Posting as {user.name}
                                </span>
                            </div>
                        </form>
                    ) : (
                        <div
                            className="p-6 mb-10 text-center"
                            style={{ background: 'var(--color-paper-100)', border: '1px solid var(--color-rule)' }}
                        >
                            <p className="text-[14.5px]" style={{ color: 'var(--color-ink-500)' }}>
                                Sign in to start a discussion or reply.
                            </p>
                            <Link
                                to={`/dashboard/login?redirect=${encodeURIComponent(`/discussion/${category}`)}`}
                                className="btn btn-gold mt-4 inline-flex"
                            >
                                Sign in to contribute
                            </Link>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-16" style={{ color: 'var(--color-ink-400)' }}>Loading discussions…</div>
                    ) : loadError ? (
                        <div className="text-center py-16">
                            <p className="text-[14px]" style={{ color: '#8B1A2D' }}>{loadError}</p>
                            <button onClick={load} className="btn btn-ghost mt-4">Retry</button>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-16" style={{ color: 'var(--color-ink-400)' }}>
                            No discussions yet. {user ? 'Be the first to post.' : 'Sign in to start one.'}
                        </div>
                    ) : (
                        <ul style={{ borderTop: '1px solid var(--color-rule)' }}>
                            {posts.map((p, i) => (
                                <motion.li
                                    key={p.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.35, delay: (i % 8) * 0.03 }}
                                    className="py-6"
                                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <Link to={`/discussion/${category}/${p.id}`} className="block group min-w-0">
                                            <div
                                                className="font-display transition-colors group-hover:text-[var(--color-accent-600)]"
                                                style={{ fontSize: 'clamp(1.1rem, 1.7vw, 1.35rem)', color: 'var(--color-ink-700)', letterSpacing: '-0.012em', lineHeight: 1.25, fontWeight: 500 }}
                                            >
                                                {p.title}
                                            </div>
                                        </Link>
                                        {canModerate(p.authorId) && (
                                            <button
                                                onClick={() => remove(p.id)}
                                                aria-label="Delete discussion"
                                                className="shrink-0 p-1.5 transition-colors"
                                                style={{ color: 'var(--color-ink-300)' }}
                                                onMouseEnter={(e) => (e.currentTarget.style.color = '#8B1A2D')}
                                                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-ink-300)')}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[12px]" style={{ color: 'var(--color-ink-400)' }}>
                                        <span className="font-semibold" style={{ color: 'var(--color-ink-500)' }}>{p.authorName}</span>
                                        <span>·</span>
                                        <span>{fmtDate(p.createdAt)}</span>
                                        <span className="inline-flex items-center gap-1">
                                            <MessageSquare size={12} /> {p.commentCount ?? 0}
                                        </span>
                                    </div>
                                    <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: 'var(--color-ink-500)' }}>
                                        {snippetOf(p.content)}
                                    </p>
                                    <Link
                                        to={`/discussion/${category}/${p.id}`}
                                        className="mt-3 inline-block text-[11px] uppercase font-semibold"
                                        style={{ letterSpacing: '0.18em', color: 'var(--color-gold-700)', borderBottom: '1px solid var(--color-gold-300)', paddingBottom: '2px' }}
                                    >
                                        Read & reply →
                                    </Link>
                                </motion.li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>
        </>
    )
}
